import React, { ReactElement } from "react";
import { FormFieldType, Permission } from "../../../__generated__/api";
import Select from "../../select/Select";

const CHOICE_LABELS = ["Group", "Options"];

export enum UserType {
    END_USER = "END_USER",
    ADMINISTRATOR = "ADMINISTRATOR"
}

export const CUSTOM_FIELD_PROPS = {

    [FormFieldType.Text]: {
        text: "Text",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.Password]: {
        text: "Password",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.LongText]: {
        text: "Long Text",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.Number]: {
        text: "Number",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },
    [FormFieldType.Decimal]: {
        text: "Decimal",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.Boolean]: {
        text: "Yes/No",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.Date]: {
        text: "Date",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.Time]: {
        text: "Time",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.DateTime]: {
        text: "Date and Time",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.AsyncDropdown]: {
        text: "Choice",
        canCreate: false,
        canEnterChoice: false,
        childLevels: 1,
        labels: CHOICE_LABELS,
        permissions: UserType.END_USER,
    },
    [FormFieldType.MultiLevelDropdown]: {
        text: "Multi-level Choice",
        canCreate: true,
        canEnterChoice: true,
        childLevels: -1,
        labels: undefined,
        permissions: UserType.END_USER,
    },
    [FormFieldType.MultiSelectDropdown]: {
        text: "Multiple Choice",
        canCreate: true,
        canEnterChoice: true,
        childLevels: 2,
        labels: CHOICE_LABELS,
        permissions: UserType.END_USER,
    },
    [FormFieldType.Dropdown]: {
        text: "Choice",
        canCreate: true,
        canEnterChoice: true,
        childLevels: 2,
        labels: CHOICE_LABELS,
        permissions: UserType.END_USER,
    },
    [FormFieldType.Checkboxes]: {
        text: "Checkboxes",
        canCreate: true,
        canEnterChoice: true,
        childLevels: 1,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.SearchText]: {
        text: "Search Text",
        canCreate: false,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },

    [FormFieldType.User]: {
        text: "User",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },
    [FormFieldType.Users]: {
        text: "Users",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.END_USER,
    },
    [FormFieldType.Json]: {
        text: "Json",
        canCreate: true,
        canEnterChoice: false,
        childLevels: 0,
        labels: [],
        permissions: UserType.ADMINISTRATOR,
    },
};

const ALL_FIELDS = Object.entries(CUSTOM_FIELD_PROPS).filter(([, row]) => row.canCreate);

const END_USER_FIELDS = ALL_FIELDS
    .filter(([, row]) => row.permissions === UserType.END_USER)
    .map(([value, row]) => ({
        label: row.text,
        value,
    }));

const ADMIN_FIELDS = ALL_FIELDS
    .filter(([, row]) => row.permissions === UserType.ADMINISTRATOR || row.permissions === UserType.END_USER)
    .map(([value, row]) => ({
        label: row.text,
        value,
    }));

export default function FieldType({
    onChange, value, className, allowedTypes, type,
}: {
    onChange: (value: FormFieldType) => void;
    value: FormFieldType;
    className?: string;
    allowedTypes?: FormFieldType[];
    type?: UserType;
}): ReactElement {
    let fields = type === UserType.ADMINISTRATOR ? ADMIN_FIELDS : END_USER_FIELDS;
    if (allowedTypes) {
        const types = new Set(allowedTypes);
        fields = fields.filter(row => types.has(row.value as FormFieldType));
    }
    return (
        <Select
            className={className}
            placeholder=""
            onChange={selected => onChange(selected.value as FormFieldType)}
            options={fields}
            value={ADMIN_FIELDS.find(option => option.value === value)}
        />
    );
}

FieldType.defaultProps = {
    className: "",
    allowedTypes: undefined,
    type: UserType.END_USER,
};
